import { ref } from 'vue';
import Pusher from 'pusher-js/with-encryption';
import useAdobeService from '@/services/adobe';
import { PUSHER_KEY, PUSHER_CLUSTER } from '@/config/appConfig';
import shouldRetryFetch from '@/utils/shouldRetryFetch';

/* ***** User Channels ***** */

const pusherInstance = ref(null); // Generic connection

function initPusher() {
  // Reuse the existing connection. Team changes move channel subscriptions on
  // the live instance instead of tearing the connection down and building a
  // new one.
  if (!pusherInstance.value) {
    pusherInstance.value = new Pusher(PUSHER_KEY, { cluster: PUSHER_CLUSTER });
  }

  return pusherInstance.value;
}

function subscribeToUserChannel(userId) {
  if (!userId) return undefined;
  return initPusher().subscribe(`user-${userId}`);
}

function unsubscribeFromUserChannel(userId) {
  if (!userId || !pusherInstance.value) return undefined;
  return pusherInstance.value.unsubscribe(`user-${userId}`);
}

/* ***** Spark Channels ***** */

const pusherInstanceSpark = ref(null); // Generic connection

function initPusherSpark() {
  if (!pusherInstanceSpark.value) {
    pusherInstanceSpark.value = new Pusher(PUSHER_KEY, { cluster: PUSHER_CLUSTER });
  }

  return pusherInstanceSpark.value;
}

function subscribeToSparkChannel(userId, businessUnitId) {
  if (!userId || !businessUnitId) return undefined;
  return initPusherSpark().subscribe(`user_${userId}_businessunit_${businessUnitId}`);
}

function unsubscribeFromSparkChannel(userId, businessUnitId) {
  if (!userId || !businessUnitId || !pusherInstanceSpark.value) return undefined;
  return pusherInstanceSpark.value.unsubscribe(`user_${userId}_businessunit_${businessUnitId}`);
}

/* ***** Adobe Channels ***** */

const pusherInstanceAdobe = ref(null); // Authed connection for encrypted Adobe notifications.

const makePusherAuth = async (pusherPayload, retryCount) => useAdobeService()
  .makePusherAuth(pusherPayload, {
    async onFetchError(error) {
    // retry if the error is a network error or a 5xx server error
      if (shouldRetryFetch(error)) {
        if (retryCount > 0) {
          setTimeout(async () => {
            await makePusherAuth(pusherPayload, retryCount - 1);
          }, 1000); // Throttle by 1 second

          return error;
        }
      }

      // because we are outside of the component when this function is called,
      // we don't have access to Toast component
      const { default: useToastsStore } = await import('@/stores/toasts');
      const { addToToastQueue } = useToastsStore();

      addToToastQueue({
        severity: 'error',
        detail: 'There is an issue with the Adobe PSD upload service. The feature may not work as expected. Please refresh or try again later.', // eslint-disable-line max-len, vue/max-len
        life: 5000,
      });

      return error;
    },
  });

/**
 * Pusher auth handler for a specific team.
 *
 * The team is captured when the connection is created and used for both the
 * channel authorization and user authentication (`signin`), so every request
 * the monolith receives carries the selected team.
 * @param {Number} teamId - Selected team/owner id.
 * @returns {Function} Pusher auth handler.
 */
const adobeCustomHandler = (teamId) => async ({ channelName, socketId }, cb) => {
  const pusherPayload = {
    socketId,
    userId: teamId,
  };

  if (channelName) {
    pusherPayload.channelName = channelName;
  }

  const {
    data: pusherAuthData,
    error: pusherAuthError,
  } = await makePusherAuth(pusherPayload, 3);

  cb(pusherAuthError?.value, pusherAuthData?.value);
};

function initAdobePusher(teamId) {
  // Rebuild the authed connection on every (re)connect. The channel is
  // owner-scoped, so a fresh connection is always created for the selected
  // team and re-runs signin + channel auth.
  disconnectPusherAdobe();

  const customHandler = adobeCustomHandler(teamId);

  pusherInstanceAdobe.value = new Pusher(PUSHER_KEY, {
    cluster: PUSHER_CLUSTER,
    enabledTransports: ['wss', 'ws', 'xhr_streaming'],
    channelAuthorization: { customHandler },
    userAuthentication: { customHandler },
  });

  pusherInstanceAdobe.value.signin();

  return pusherInstanceAdobe.value;
}

function subscribeToAdobeNotifications(userId) {
  if (!userId || !pusherInstanceAdobe.value) return undefined;
  return pusherInstanceAdobe.value.subscribe(`private-encrypted-notifications_${userId}`);
}

function unsubscribeFromAdobeNotifications(userId) {
  if (!userId || !pusherInstanceAdobe.value) return undefined;
  return pusherInstanceAdobe.value.unsubscribe(`private-encrypted-notifications_${userId}`);
}

/* ***** Connection lifecycle ***** */

function disconnectPusher() {
  if (pusherInstance.value) {
    pusherInstance.value.disconnect();
    pusherInstance.value = null;
  }
}

function disconnectPusherSpark() {
  if (pusherInstanceSpark.value) {
    pusherInstanceSpark.value.disconnect();
    pusherInstanceSpark.value = null;
  }
}

function disconnectPusherAdobe() {
  if (pusherInstanceAdobe.value) {
    pusherInstanceAdobe.value.disconnect();
    pusherInstanceAdobe.value = null;
  }
}

/**
 * Close every Pusher connection. Called when leaving the logged-in layout.
 */
function disconnect() {
  disconnectPusher();
  disconnectPusherSpark();
  disconnectPusherAdobe();
}

/* ***** Exports ***** */

export default () => {
  const init = (teamId) => {
    const user = initPusher();
    const adobe = initAdobePusher(teamId);
    const spark = initPusherSpark();

    return {
      adobe,
      user,
      spark,
    };
  };

  return {
    instances: {
      adobe: pusherInstanceAdobe?.value || pusherInstanceAdobe,
      user: pusherInstance?.value || pusherInstance,
      spark: pusherInstanceSpark?.value || pusherInstanceSpark,
    },

    init,
    disconnect,
    disconnectAdobe: disconnectPusherAdobe,

    subscribeToAdobeNotifications,
    unsubscribeFromAdobeNotifications,

    subscribeToUserChannel,
    unsubscribeFromUserChannel,

    subscribeToSparkChannel,
    unsubscribeFromSparkChannel,
  };
};
